import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiError, HttpService } from '../../../services/http.service';
import { InputComponent } from '../../from-input/input.component';
import { MultiSelectComponent } from '../../multi-select/multi-select.component';
import { CustomToastComponent } from '../../toast/custom-toast.component';
import { finalize } from 'rxjs';
import { environment } from '../../../../../environments/environment';

interface EmailData {
  fromEmail: string;
  toEmail: string;
  ccEmail: string;
  composeMail: string;
  id: string;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: any;
}

interface ApiDataResponse {
  status: boolean;
  message: string;
  data: APIResponse;
}

@Component({
  selector: 'app-send-email-dialog',
  standalone: true,
  templateUrl: './send-email-dialog.component.html',
  styleUrls: ['./send-email-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    MultiSelectComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SendEmailDialogComponent implements OnInit {
  @ViewChild('emailEditor') emailEditor!: ElementRef<HTMLDivElement>;
  @ViewChild(MultiSelectComponent) multiSelectComponent!: MultiSelectComponent;
  form!: FormGroup;
  loading = false;
  recipientEmailOptions: any[] = [];
  isToRequired = false;

  readonly customErrorMessages = {
    fromEmail: {
      required: 'Please enter a from email.',
      email: 'Please enter a valid email address',
    },
    toEmail: {
      required: 'Please enter a to email.',
      email: 'Please enter a valid email address',
    },
    ccEmail: {
      email: 'Please enter a valid email address',
    },
    subject: {
      required: 'Please enter a subject.',
    },
    composeMail: {
      required: 'Please enter a email content.',
    },
  };

  constructor(
    private fb: FormBuilder,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<SendEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { initialData?: EmailData; eventData: any },
  ) {
    this.initForm();
  }

  private setSafeHtml(content: string): void {
    if (!this.emailEditor) return;
    this.emailEditor.nativeElement.innerHTML =
      this.sanitizer.sanitize(SecurityContext.HTML, content) ?? '';
  }

  ngOnInit(): void {
    if (this.data?.initialData) {
      this.fetchRecipientEmails(this.data.initialData.id);
      this.patchFormWithInitialData();
    }
  }

  ngAfterViewInit(): void {
    const initialContent = this.form.get('composeMail')?.value;
    if (initialContent) {
      this.setSafeHtml(initialContent);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      fromEmail: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      toEmail: [[]],
      ccEmail: [
        '',
        [
          Validators.pattern(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          ),
        ],
      ],
      subject: ['', [Validators.required]],
      composeMail: ['', Validators.required],
    });
  }

  fetchRecipientEmails(id: string): void {
    this.httpService
      .get<APIResponse>(`events/${id}/teachers-for-mail`)
      .subscribe({
        next: (response) => {
          this.recipientEmailOptions = Array.isArray(response.data)
            ? response.data.map((item) => ({
                label: item.email,
                value: item.email,
                name: item?.name,
              }))
            : [];
        },
        error: (err) => {
          this.showErrorToast(
            err?.message || 'Something went wrong. Please try again later.',
          );
        },
      });
  }

  private patchFormWithInitialData(): void {
    const initialData = this.data.initialData;
    const eventData = this.data.eventData;

    // console.log(eventData);

    // console.log(eventData,"eventData");

    if (initialData) {
      const date = new Date(eventData.eventStartDate);
      // const composeMailContent = `
      //     <p>Dear [Teacher's Name],</p>
      //     <p>We're excited to let you know that a new Construction Pathway event has been published and is now open for student registrations!</p>
      //     <p><strong>Event Details:</strong></p>
      //     <ul>
      //     <li><strong>Event Name:</strong> ${eventData.eventName}</li>
      //     <li><strong>Date:</strong> ${date.toLocaleDateString('en-GB')}</li>
      //     <li><strong>Time:</strong>  ${eventData.eventTime}</li>
      //     <li><strong>Location:</strong>  ${eventData.location}</li>
      //     <li><strong>Event Type:</strong>  ${eventData.eventTypeName}</li>
      //     <li><strong>Description:</strong>  ${eventData.description}</li>
      //     </ul>
      //     <p><strong>Next Steps for You:</strong></p>
      //     <p>Please share the registration link below with your students and encourage them to register as soon as possible:</p>
      //     <p>[Unique Registration Link Will Generate Here]</p>
      //     <p>Alternatively, you can register students on their behalf through the portal.</p>
      //     <p><strong>Why It Matters:</strong></p>
      //     <p>Registering ensures students receive credit for their participation and gain access to their Student Experience Passport, where they can track professional development, view download certificates, and build a stronger pathway into the construction industry.</p>
      //     <p>If you have any questions or need support with the registration process, feel free to reach out.</p>
      //     <p>Thank you for supporting your students' future careers in construction!</p>
      //     <p>Best regards,<br>
      //     CSQ Team<br>
      //     Contact Info</p>`;

      const options = Array.isArray(eventData.managers)
        ? eventData.managers
        : [];

      const managerContact = options
        // .map((opt : any) => opt?.mobileNumber)
        .map(
          (opt: any) =>
            `<a href="tel:${opt?.mobileNumber}" target="_blank">${opt?.mobileNumber}</a>`,
        )
        .filter(Boolean);

      const contactNames = managerContact.length
        ? managerContact.join(', ')
        : 'CSQ';

      const composeMailContent = `
                    <p>Hi,</p>
                                
                    <p>
                        Your event <strong>${eventData.eventName}</strong> has been successfully created
                        in the portal - exciting! 🎉
                    </p>
                                
                    <p>Here's a quick snapshot of the key details:</p>
                                
                    <p><strong>📅 Event Summary</strong></p>
                                
                    <ul>
                        <li><strong>Date:</strong> ${date.toLocaleDateString('en-GB')}</li>
                        <li><strong>Time:</strong> ${eventData.eventTime}</li>
                        <li><strong>Location:</strong> ${eventData.location}</li>
                        <li>
                        <strong>Program:</strong>
                        ${eventData.eventTypeName} - ${eventData.eventName}
                        </li>
                    </ul>
                                
                    <p>
                        <strong>Note:</strong> We'll send updates if anything changes for your event.
                    </p>
                                
                    <p><strong>🔗 Manage Your Event</strong></p>
                                
                    <p>
                        You can view, update, or manage your event here:
                        <br />
                        <a href="${this.determineFeUrl()}" target="_blank">CSQ Portal</a>
                    </p>
                                
                    <p><strong>👥 Share With Your Students</strong></p>
                                
                    <p>Students can now register for this event. You can:</p>
                                
                    <ul>
                        <li>Share the event link with them directly through the portal, or</li>
                        <li>
                        Copy the event details and send them to students via your own email or
                        communication channels.
                        </li>
                    </ul>
                                
                    <p><strong>🛠️ Need Help?</strong></p>
                                
                    <p>
                        If you need to adjust details or require support, contact us anytime at
                        ${contactNames}.
                    </p>
                                
                    <p>
                        Thanks,<br />
                        CSQ - Construction Pathways Team
                    </p>
                    `;

      this.form.patchValue({
        fromEmail: initialData.fromEmail || '',
        ccEmail: ['constructionpathways@gmail.com'],
        subject: `Upcoming CSQ Event, ${eventData.eventName} Has Been Created`,
        composeMail: composeMailContent,
      });
      setTimeout(() => {
        this.setSafeHtml(composeMailContent);
      }, 0);
    }
  }

  onRecipientsChange(selectedEmails: any): void {
    this.form.patchValue({
      toEmail: selectedEmails,
    });
  }

  onEditorInput(event: Event): void {
    const content = this.emailEditor.nativeElement.innerHTML;
    this.form.patchValue(
      {
        composeMail: content,
      },
      { emitEvent: false },
    );

    this.validateEditorContent(content);
  }

  onEditorBlur(): void {
    const content = this.emailEditor.nativeElement.innerHTML;
    this.form.patchValue(
      {
        composeMail: content,
      },
      { emitEvent: false },
    );
    this.validateEditorContent(content);
    this.form.get('composeMail')?.markAsTouched();
  }

  private validateEditorContent(content: string): void {
    const cleanContent = content.replace(/<br>|&nbsp;|<p><\/p>|\s/g, '').trim();
    if (cleanContent === '' || cleanContent === '<p></p>') {
      this.form.get('composeMail')?.setErrors({ required: true });
    } else {
      this.form.get('composeMail')?.setErrors(null);
    }
  }

  formatText(command: string, value: string = ''): void {
    this.emailEditor.nativeElement.focus();
    document.execCommand(command, false, value);

    const content = this.emailEditor.nativeElement.innerHTML;
    this.form.patchValue(
      {
        composeMail: content,
      },
      { emitEvent: false },
    );

    this.validateEditorContent(content);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  private determineApiUrl(): string {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://web-trex-dev-api.azurewebsites.net/api';
    }
    if (hostname === '192.168.1.7') {
      return 'http://192.168.1.7:8080/api';
    }
    if (hostname === '192.168.29.15' || hostname === '192.168.29.12') {
      return 'http://192.168.29.12:8080/api';
    }
    if (
      hostname === 'web-trex-dev-fe.azurewebsites.net'
    ) {
      return 'https://web-trex-dev-api.azurewebsites.net/api';
    }

    if (hostname === 'web-trex-prd-fe-staging.azurewebsites.net') {
      return 'https://web-trex-prd-api-staging.azurewebsites.net/api';
    }

    if (hostname === 'trex.constructionpathways.com.au') {
      return 'https://web-trex-prd-api.azurewebsites.net/api';
    }

    console.log('Running on UNKNOWN environment, using staging as fallback');
    return 'web-trex-prd-api.azurewebsites.net';
  }

  private determineFeUrl(): string {
    const hostname = window.location.hostname;

    if (hostname === 'web-trex-prd-fe-staging.azurewebsites.net') {
      return 'https://web-trex-prd-fe-staging.azurewebsites.net';
    }
    if (hostname === 'trex.constructionpathways.com.au') {
      return 'https://trex.constructionpathways.com.au';
    }
    return 'https://web-trex-dev-fe.azurewebsites.net';
  }

  onSave(): void {
    this.loading = true;

    const content = this.emailEditor.nativeElement.innerHTML;
    this.form.patchValue({ composeMail: content });

    this.validateEditorContent(content);

    const toEmailValue = this.form.get('toEmail')?.value;

    if (!toEmailValue || toEmailValue.length === 0) {
      this.isToRequired = true;
      this.form.get('toEmail')?.setErrors({ required: true });
      this.form.get('toEmail')?.markAsTouched();

      this.multiSelectComponent?.markAsTouched();
      this.multiSelectComponent?.markAsSubmitted();

      this.loading = false;
      this.cdr.detectChanges();
      return;
    } else {
      this.isToRequired = false;
    }
    this.form.markAllAsTouched();

    if (this.form.valid) {
      const formValues = this.form.getRawValue();

      const teachers = Array.isArray(formValues.toEmail)
        ? formValues.toEmail
        : formValues.toEmail
          ? [formValues.toEmail]
          : [];

      const options = Array.isArray(this.recipientEmailOptions)
        ? this.recipientEmailOptions
        : [];

      const selectedNames = options
        .filter((opt) => opt?.value && teachers.includes(opt.value))
        .map((opt) => opt?.name)
        .filter(Boolean);

      const teacherNamesText = selectedNames.length
        ? selectedNames.join(', ')
        : 'Teacher';

      const apiURL = this.determineApiUrl();

      const link = `<a href="${apiURL}/events/event-list/view/${this.data?.initialData?.id}">Go to event</a>`;

      let body = formValues.composeMail;
      body = `
                    
                    <body style="margin:0; padding:0; background-color:#f3f7f2; font-family:Arial, sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" border="0" style="padding:32px 0;">
                    <style>
                        .email-body ul { margin:0 0 16px; padding-left:20px; }
                        .email-body li { margin-bottom:6px; }
                        .email-body a { color:#ff6a21; text-decoration:underline; }
                    </style> 
                    <tr> <td align="center">  <table width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%; max-width:600px; background-color:#eef6ea; border-radius:6px; overflow:hidden;"> <tr> <td style="padding:32px; font-size:15px; line-height:1.7; color:#1f2937;">  <h2 style=" margin:0 0 18px; color: #f57c00; font-size: 20px; font-weight: 700; font-family: Arial, sans-serif;  text-align: left;"> ${formValues.subject} </h2> <div class="email-body">${body}</div> </td></tr> </table> </td></tr> </table></body>`;

      body = body?.replace(
        '[Unique Registration Link Will Generate Here]',
        link,
      );

      const formData = new FormData();
      formData.append('fromEmail', formValues.fromEmail || '');
      formData.append('to', JSON.stringify(formValues.toEmail || []));
      formData.append(
        'cc',
        JSON.stringify(
          typeof formValues.ccEmail === 'string'
            ? [formValues.ccEmail]
            : formValues.ccEmail || [],
        ),
      );
      formData.append('subject', formValues.subject || '');
      formData.append('body', body || '');
      formData.append('teacherNames', teacherNamesText || '');

      // console.log('Sending email with FormData:');
      // console.log('fromEmail:', formValues.fromEmail);
      // console.log('to:', formValues.toEmail);
      // console.log('cc:', formValues.ccEmail);
      // console.log('subject:', formValues.subject);
      // console.log('body:', formValues.composeMail);
      // this.loading = false;
      // return

      this.httpService
        .post<ApiDataResponse>(
          `events/${this.data?.initialData?.id}/send-mail`,
          formData,
          true,
        )
        .pipe(
          finalize(() => {
            this.loading = false;
          }),
        )
        .subscribe({
          next: (response) => {
            this.handleSubmitResponse(response);
            if (response?.status) {
              this.loading = false;
              this.onCancel();
            }
          },
          error: (err: ApiError) => {
            this.loading = false;
            this.showErrorToast(
              err?.message || 'Something went wrong. Please try again later.',
            );
          },
        });
    } else {
      this.loading = false;
      this.scrollToFirstInvalidField();
    }
  }

  private scrollToFirstInvalidField(): void {
    const firstInvalidControl = document.querySelector('.ng-invalid');
    if (firstInvalidControl) {
      firstInvalidControl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  private handleSubmitResponse(response: ApiDataResponse): void {
    if (response.status) {
      this.showSuccessToast(response.message || 'Email sent successfully');
      this.dialogRef.close({ success: true, data: response.data });
    } else {
      this.showErrorToast(
        response.message || 'Something went wrong. Please try again later.',
      );
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }
}
